import { ChainedCommands, Editor } from '@tiptap/core'
import { Node as ProseMirrorNode, ResolvedPos } from '@tiptap/pm/model'
import { EditorState, Transaction } from '@tiptap/pm/state'
import {
  FormattingChanges,
  LIST_ITEM_NODE_NAME,
  LIST_TYPE_BULLET,
  LIST_TYPE_ORDERED,
  ListChange,
  ListIndentSource,
  ListType,
  SUGGESTION_MARK_NAME,
  SUGGESTION_TYPE_FORMATTING,
} from '../../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'
import {
  buildSuggestionAttrs,
  createBaseAttrs,
  findAdjacentSuggestionAttrs,
  findFormattingSuggestionAtExactRange,
  mergeFormattingChanges,
  rangeIsInsideTextSuggestion,
  TRACK_CHANGES_SKIP_META,
  trackChangesPluginKey,
  TrackChangesUser,
} from './track-changes-plugin'

const LIST_NODE_TYPE_NAMES = new Set<string>([LIST_TYPE_BULLET, LIST_TYPE_ORDERED])
const PARAGRAPH_NODE_NAME = 'paragraph'

export type ListItemState = {
  listType: ListType | null
  depth: number
}

export type ListItemBeforeAfter = {
  before: ListItemState
  after: ListItemState
}

export type CapturedItem = {
  contentFrom: number
  contentTo: number
  state: ListItemState
}

export function nearestListInfoForPosition(doc: ProseMirrorNode, position: number): ListItemState {
  const resolved = doc.resolve(Math.min(Math.max(position, 0), doc.content.size))
  let nearestListType: ListType | null = null
  let depth = 0

  for (let depthIndex = resolved.depth; depthIndex > 0; depthIndex--) {
    const ancestor = resolved.node(depthIndex)
    if (LIST_NODE_TYPE_NAMES.has(ancestor.type.name)) {
      depth += 1
      if (nearestListType === null) {
        nearestListType = ancestor.type.name as ListType
      }
    }
  }

  return { listType: nearestListType, depth }
}

export function captureParagraphsInRange(state: EditorState, from: number, to: number): CapturedItem[] {
  const items: CapturedItem[] = []
  const clampedFrom = Math.min(Math.max(from, 0), state.doc.content.size)
  const clampedTo = Math.min(Math.max(to, clampedFrom), state.doc.content.size)
  // Ensure even a collapsed selection enumerates its containing paragraph by
  // probing one position past the cursor.
  const probeTo = clampedTo === clampedFrom ? Math.min(clampedTo + 1, state.doc.content.size) : clampedTo

  state.doc.nodesBetween(clampedFrom, probeTo, (node, position) => {
    if (node.type.name === PARAGRAPH_NODE_NAME) {
      const contentFrom = position + 1
      const contentTo = position + node.nodeSize - 1
      items.push({ contentFrom, contentTo, state: nearestListInfoForPosition(state.doc, contentFrom) })
      return false
    }
    return true
  })

  return items
}

export type MappedItem = {
  before: ListItemState
  after: ListItemState
  contentFrom: number
  contentTo: number
}

export function mapCapturedItemsThroughTransaction(items: CapturedItem[], transaction: Transaction): MappedItem[] {
  // `liftListItemSingleLevel` moves a listItem via delete+insert, which the PM
  // mapping can't track — when the captured paragraph belongs to that lifted
  // item we use the explicit move-info instead of the mapping.
  const moveInfo = (transaction.getMeta(LIST_TRACK_CHANGES_MOVE_META) as ListItemMoveInfo | null | undefined) ?? null
  return items.map((item) => {
    let mappedFrom: number
    let mappedTo: number
    if (moveInfo !== null && item.contentFrom === moveInfo.capturedContentFrom) {
      mappedFrom = moveInfo.newContentFrom
      mappedTo = moveInfo.newContentTo
    } else {
      mappedFrom = transaction.mapping.map(item.contentFrom, 1)
      mappedTo = transaction.mapping.map(item.contentTo, -1)
    }
    return {
      before: item.state,
      after: nearestListInfoForPosition(transaction.doc, mappedFrom),
      contentFrom: mappedFrom,
      contentTo: mappedTo,
    }
  })
}

// The four toolbar commands each produce only a list-type change OR a depth
// change, never both — `wrap`/`unwrap`/`changeListType` cover the type-changing
// commands and `indent` covers the depth-changing ones. We deliberately only
// emit one change per item; mixing both would never appear from user actions.
export function classifyChange(before: ListItemState, after: ListItemState): ListChange | null {
  if (before.listType === null && after.listType === null) {
    return null
  }
  if (before.listType === null && after.listType !== null) {
    return { kind: 'wrap', listType: after.listType }
  }
  if (before.listType !== null && after.listType === null) {
    return { kind: 'unwrap', listType: before.listType }
  }
  if (before.listType !== null && after.listType !== null && before.listType !== after.listType) {
    return { kind: 'changeListType', from: before.listType, to: after.listType }
  }
  if (before.depth !== after.depth) {
    return { kind: 'indent', delta: after.depth - before.depth }
  }
  return null
}

export function listChangeEqual(left: ListChange, right: ListChange): boolean {
  if (left.kind !== right.kind) {
    return false
  }
  if (left.kind === 'wrap' && right.kind === 'wrap') {
    return left.listType === right.listType
  }
  if (left.kind === 'unwrap' && right.kind === 'unwrap') {
    return left.listType === right.listType
  }
  if (left.kind === 'changeListType' && right.kind === 'changeListType') {
    return left.from === right.from && left.to === right.to
  }
  if (left.kind === 'indent' && right.kind === 'indent') {
    return left.delta === right.delta
  }
  return false
}

export function listChangeArraysEqual(left: ListChange[], right: ListChange[]): boolean {
  return left.length === right.length && left.every((value, index) => listChangeEqual(value, right[index]))
}

export type ListCommandTrackingOptions = {
  getEnabled: () => boolean
  getCurrentUser: () => TrackChangesUser | null
}

// Manual delete+insert breaks PM mapping, so the lift exposes the moved item's
// new content range and original slot via this meta channel. Only consumed
// within this module, so kept private.
const LIST_TRACK_CHANGES_MOVE_META = 'listTrackChangesMove'

type ListItemMoveInfo = {
  newContentFrom: number
  newContentTo: number
  capturedContentFrom: number
  source: ListIndentSource
}

// Walks up from `$pos` to find the deepest containing listItem. Returns -1 if
// `$pos` isn't inside any listItem.
function findContainingListItemDepth($pos: ResolvedPos): number {
  for (let depth = $pos.depth; depth >= 1; depth--) {
    if ($pos.node(depth).type.name === LIST_ITEM_NODE_NAME) {
      return depth
    }
  }
  return -1
}

// Range covering a listItem to detach. When the listItem is the only child of
// its parent list, the range expands to the parent list — leaving an empty list
// behind would violate the list's content schema.
function detachRangeForListItem($pos: ResolvedPos, listItemDepth: number): { from: number; to: number } {
  const parentList = $pos.node(listItemDepth - 1)
  if (parentList.childCount === 1) {
    return { from: $pos.before(listItemDepth - 1), to: $pos.after(listItemDepth - 1) }
  }
  return { from: $pos.before(listItemDepth), to: $pos.after(listItemDepth) }
}

// Lift just the listItem containing the cursor up one level — without PM's
// `liftListItem` extras (trailing-sibling wrapping and a join with the previous
// sibling) that prevent `sinkListItem` from cleanly reversing in our schema.
// The targeted listItem is detached and re-inserted right after its grand-listItem
// in the surrounding list; trailing siblings stay where they were.
export function liftListItemSingleLevel(props: { state: EditorState; tr: Transaction; dispatch?: (tr: Transaction) => void }): boolean {
  const { state, tr, dispatch } = props
  const { $from } = state.selection

  const listItemDepth = findContainingListItemDepth($from)
  if (listItemDepth < 2) {
    return false
  }
  const parentList = $from.node(listItemDepth - 1)
  if (parentList.type.name !== LIST_TYPE_BULLET && parentList.type.name !== LIST_TYPE_ORDERED) {
    return false
  }
  if ($from.node(listItemDepth - 2).type.name !== LIST_ITEM_NODE_NAME) {
    return false
  }

  if (dispatch === undefined) {
    return true
  }

  const sunkItem = $from.node(listItemDepth)
  const capturedContentFrom = $from.before(listItemDepth) + 2
  const insertAfter = $from.after(listItemDepth - 2)
  const detach = detachRangeForListItem($from, listItemDepth)
  const wrap = parentList.childCount === 1 ? { type: parentList.type.name as ListType, attrs: parentList.attrs } : undefined

  tr.delete(detach.from, detach.to)
  const insertPos = tr.mapping.map(insertAfter, -1)
  tr.insert(insertPos, sunkItem)

  const moveInfo: ListItemMoveInfo = {
    newContentFrom: insertPos + 2,
    newContentTo: insertPos + 2 + (sunkItem.firstChild?.content.size ?? 0),
    capturedContentFrom,
    source: { position: tr.mapping.map(detach.from, -1), wrap },
  }
  tr.setMeta(LIST_TRACK_CHANGES_MOVE_META, moveInfo)
  return true
}

// Move the listItem containing the cursor back to a captured source slot.
// Inverts `liftListItemSingleLevel` cleanly: the slot — and the parent list to
// recreate when it was emptied — was recorded on the suggestion mark at
// forward time.
export function restoreListItemAtSource(source: ListIndentSource) {
  return (props: { state: EditorState; tr: Transaction; dispatch?: (tr: Transaction) => void }): boolean => {
    const { state, tr, dispatch } = props
    const { $from } = state.selection

    const listItemDepth = findContainingListItemDepth($from)
    if (listItemDepth < 1) {
      return false
    }
    if (dispatch === undefined) {
      return true
    }

    const sunkItem = $from.node(listItemDepth)
    const detach = detachRangeForListItem($from, listItemDepth)

    tr.delete(detach.from, detach.to)
    const insertPos = tr.mapping.map(source.position, -1)
    if (source.wrap !== undefined) {
      const wrappingType = state.schema.nodes[source.wrap.type] ?? null
      if (wrappingType === null) {
        return false
      }
      tr.insert(insertPos, wrappingType.create(source.wrap.attrs, sunkItem))
    } else {
      tr.insert(insertPos, sunkItem)
    }
    return true
  }
}

// Captures the affected paragraphs' before-state, runs the list command in a chain,
// derives the per-item ListChange[], and stamps a `formatting` suggestion mark on the
// inline range so list edits show up in track-changes alongside inline edits. When
// track changes is off (or the user is unknown), the command runs unwrapped.
export function runListCommandWithTracking(
  editor: Editor,
  options: ListCommandTrackingOptions,
  applyCommand: (chain: ChainedCommands) => ChainedCommands
): boolean {
  const currentUser = options.getCurrentUser()
  if (!options.getEnabled() || currentUser === null) {
    return applyCommand(editor.chain().focus()).run()
  }

  const { from, to } = editor.state.selection
  const capturedItems = captureParagraphsInRange(editor.state, from, to)

  return applyCommand(editor.chain().focus())
    .command(({ tr, dispatch }) => {
      if (dispatch === undefined) {
        return true
      }
      if (!tr.docChanged) {
        tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        return true
      }

      const mappedItems = mapCapturedItemsThroughTransaction(capturedItems, tr)
      const changedItems = mappedItems.filter((item) => classifyChange(item.before, item.after) !== null)
      if (changedItems.length === 0) {
        tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        return true
      }

      const rawListChanges = predictListChanges(changedItems.map((item) => ({ before: item.before, after: item.after })))
      if (rawListChanges.length === 0) {
        tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        return true
      }
      // Capture where the lifted listItem sat in the source list. The reject
      // path moves the item back to this exact slot, so trailing siblings — and
      // the lifted item itself — stay at their original positions.
      const moveInfo = readMoveInfo(tr)
      const listChanges =
        moveInfo === null ? rawListChanges : rawListChanges.map((change) => (change.kind === 'indent' ? { ...change, source: moveInfo.source } : change))

      let rangeFrom = Number.POSITIVE_INFINITY
      let rangeTo = Number.NEGATIVE_INFINITY
      for (const item of changedItems) {
        if (item.contentFrom < rangeFrom) {
          rangeFrom = item.contentFrom
        }
        if (item.contentTo > rangeTo) {
          rangeTo = item.contentTo
        }
      }

      if (rangeIsInsideTextSuggestion(tr.doc, rangeFrom, rangeTo)) {
        tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        return true
      }

      const suggestionMarkType = tr.doc.type.schema.marks[SUGGESTION_MARK_NAME] ?? null
      if (suggestionMarkType === null) {
        tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        return true
      }

      const existing = findFormattingSuggestionAtExactRange(tr.doc, rangeFrom, rangeTo, currentUser.id)
      if (existing !== null) {
        const merged = mergeFormattingChanges(existing.formattingChanges, { added: new Set(), removed: new Set(), listChanges })
        tr.removeMark(rangeFrom, rangeTo, existing.mark)
        if (merged !== null) {
          const attrs = { ...buildSuggestionAttrs(SUGGESTION_TYPE_FORMATTING, existing.baseAttrs), formattingChanges: merged }
          tr.addMark(rangeFrom, rangeTo, suggestionMarkType.create(attrs))
        }
      } else {
        const baseAttrs = findAdjacentSuggestionAttrs(tr.doc, rangeFrom, rangeTo, currentUser.id) ?? createBaseAttrs(currentUser)
        const formatting: FormattingChanges = { added: [], removed: [], listChanges }
        const attrs = { ...buildSuggestionAttrs(SUGGESTION_TYPE_FORMATTING, baseAttrs), formattingChanges: formatting }
        tr.addMark(rangeFrom, rangeTo, suggestionMarkType.create(attrs))
      }

      tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
      return true
    })
    .run()
}

// Returns the move-info recorded by the custom `liftListItemSingleLevel` for
// this transaction, or null if the forward op didn't record one (e.g. PM's
// built-in `sinkListItem`).
function readMoveInfo(transaction: Transaction): ListItemMoveInfo | null {
  return (transaction.getMeta(LIST_TRACK_CHANGES_MOVE_META) as ListItemMoveInfo | null | undefined) ?? null
}

export function predictListChanges(items: ListItemBeforeAfter[]): ListChange[] {
  const collapsed: ListChange[] = []
  for (const item of items) {
    const change = classifyChange(item.before, item.after)
    if (change === null) {
      continue
    }
    const alreadyPresent = collapsed.some((existing) => listChangeEqual(existing, change))
    if (!alreadyPresent) {
      collapsed.push(change)
    }
  }
  return collapsed
}

function inverseListChange(change: ListChange): ListChange {
  if (change.kind === 'wrap') {
    return { kind: 'unwrap', listType: change.listType }
  }
  if (change.kind === 'unwrap') {
    return { kind: 'wrap', listType: change.listType }
  }
  if (change.kind === 'changeListType') {
    return { kind: 'changeListType', from: change.to, to: change.from }
  }
  return { kind: 'indent', delta: -change.delta }
}

function isNetCancellation(left: ListChange, right: ListChange): boolean {
  const inverse = inverseListChange(left)
  return listChangeEqual(inverse, right)
}

// Concatenate two listChange arrays, collapsing pairs where one inverts the other.
// Multiple `indent` entries combine by summing their deltas; an indent with net zero
// is dropped.
export function combineListChanges(existing: ListChange[], incoming: ListChange[]): ListChange[] {
  const combined: ListChange[] = []
  for (const change of [...existing, ...incoming]) {
    if (change.kind === 'indent') {
      const existingIndentIndex = combined.findIndex((entry) => entry.kind === 'indent')
      if (existingIndentIndex !== -1) {
        const previousIndent = combined[existingIndentIndex] as { kind: 'indent'; delta: number }
        const totalDelta = previousIndent.delta + change.delta
        if (totalDelta === 0) {
          combined.splice(existingIndentIndex, 1)
        } else {
          combined[existingIndentIndex] = { kind: 'indent', delta: totalDelta }
        }
        continue
      }
      combined.push(change)
      continue
    }

    const cancellingIndex = combined.findIndex((entry) => isNetCancellation(entry, change))
    if (cancellingIndex !== -1) {
      combined.splice(cancellingIndex, 1)
      continue
    }
    combined.push(change)
  }
  return combined
}
